import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import {
  useListCustomerAddresses,
  useCreateCustomerAddress,
  useUpdateCustomerAddress,
  useDeleteCustomerAddress,
  useSetCustomerAddressFreeze,
  useSetCustomerShowPricing,
  useAssignCustomerToSalesRep,
  type CustomerAddressesResponse,
  type CustomerAddressProduct,
} from '../../features/customers/api';

import AddressAutocomplete from '../../components/AddressAutocomplete/AddressAutocomplete';
import SearchInput from '../../components/SearchInput/SearchInput';
import OperatingHoursEditor from '../../components/OperatingHoursEditor/OperatingHoursEditor';
import { formatPhone } from '../../utils/formatters';
import { useListOrgMembers } from '../../features/org-members/api';
import { useCurrentUser } from '../../features/auth/api';

// Form rules — pure, unit-tested, no React.
// See features/addresses/addressForm.
import {
  buildAddressPayload,
  buildFormFromAddress,
  clearCarrierRate as applyClearCarrierRate,
  countConfiguredRates,
  createEmptyForm,
  deleteErrorMessage,
  filterWarehouses,
  getConfiguredRateCount,
  centsToDollarsInput,
  dollarsInputToCents,
  getCustomerUserDisplayName,
  saveErrorMessage,
  sortAndFilterLoadOptions,
  toggleLoadOption as applyToggleLoadOption,
  updateCarrierRate as applyCarrierRate,
  validateAddressForm,
} from '../../features/addresses/addressForm';
import {
  resolveRegion,
  REGION_OPTIONS,
  REGION_LABELS,
  type PricingRegion,
} from '../../features/pricing';

import fallbackProductImage from '../../assets/defeo-logo.png';
import './customer-addresses-tier.scss';

interface CustomerAddressesModalProps {
  customerId: string;
  onClose: () => void;
  /** Return to the parent customer record. When set, a "Back to customer" control is shown on the list view. */
  onBack?: () => void;
}

type ProductAccessState = 'ENABLED' | 'PREVIEW' | 'DISABLED';

type LocationProductAssignment = {
  productId: string;
  isEnabled: boolean;
  isPreview: boolean;
  deliveredPriceCents?: number | null;
};

type ProductAccessMap = Record<string, ProductAccessState>;
type ProductApplyToAllMap = Record<string, boolean>;
/** productId -> Northeast delivered price/ton as a dollar string. */
type ProductPriceMap = Record<string, string>;

function buildProductPriceFromAddress(row: any): ProductPriceMap {
  const result: ProductPriceMap = {};
  const assignments: LocationProductAssignment[] = Array.isArray(row?.products) ? row.products : [];
  for (const assignment of assignments) {
    if (!assignment?.productId) continue;
    result[assignment.productId] = centsToDollarsInput(assignment.deliveredPriceCents);
  }
  return result;
}

function buildProductAccessFromAddress(row: any): ProductAccessMap {
  const result: ProductAccessMap = {};

  const assignments: LocationProductAssignment[] = Array.isArray(row?.products) ? row.products : [];

  for (const assignment of assignments) {
    if (!assignment?.productId) {
      continue;
    }

    if (assignment.isEnabled) {
      result[assignment.productId] = 'ENABLED';
      continue;
    }

    if (assignment.isPreview) {
      result[assignment.productId] = 'PREVIEW';
    }
  }

  return result;
}

function buildProductsPayload(
  productAccess: ProductAccessMap,
  productPrice: ProductPriceMap
): LocationProductAssignment[] {
  return Object.entries(productAccess)
    .filter(([, access]) => access !== 'DISABLED')
    .map(([productId, access]) => ({
      productId,
      isEnabled: access === 'ENABLED',
      isPreview: access === 'PREVIEW',
      deliveredPriceCents: dollarsInputToCents(productPrice[productId]) || null,
    }));
}

function getProductAccess(productAccess: ProductAccessMap, productId: string): ProductAccessState {
  return productAccess[productId] ?? 'DISABLED';
}

function getEnabledProductIds(productAccess: ProductAccessMap) {
  return Object.entries(productAccess)
    .filter(([, access]) => access === 'ENABLED')
    .map(([productId]) => productId);
}

function getAvailableWarehouseIdsForProducts(
  products: CustomerAddressProduct[],
  productAccess: ProductAccessMap
) {
  const enabledProductIds = new Set(getEnabledProductIds(productAccess));

  const warehouseIds = new Set<string>();

  for (const product of products) {
    if (!enabledProductIds.has(product.id)) {
      continue;
    }

    for (const warehouseId of product.warehouseIds ?? []) {
      warehouseIds.add(warehouseId);
    }
  }

  return warehouseIds;
}

function normalizeDraftValue(value: any): any {
  if (Array.isArray(value)) {
    return value.map(normalizeDraftValue);
  }

  if (value && typeof value === 'object') {
    return Object.keys(value)
      .sort()
      .reduce<Record<string, any>>((result, key) => {
        result[key] = normalizeDraftValue(value[key]);
        return result;
      }, {});
  }

  return value;
}

function buildEditorSignature(input: {
  form: any;
  salesRepUserId: string;
  applyCustomerTierDiscount: boolean;
  productAccess: ProductAccessMap;
  applyProductAccessToAllLocations: ProductApplyToAllMap;
  region?: string;
  productPrice?: ProductPriceMap;
}) {
  return JSON.stringify(
    normalizeDraftValue({
      form: input.form,
      salesRepUserId: input.salesRepUserId,
      applyCustomerTierDiscount: input.applyCustomerTierDiscount,
      productAccess: input.productAccess,
      applyProductAccessToAllLocations: input.applyProductAccessToAllLocations,
      region: input.region ?? '',
      productPrice: input.productPrice ?? {},
    })
  );
}

export default function CustomerAddressesModal({
  customerId,
  onClose,
  onBack,
}: CustomerAddressesModalProps) {
  const { data, isLoading } = useListCustomerAddresses(customerId);

  const createAddr = useCreateCustomerAddress(customerId);
  const updateAddr = useUpdateCustomerAddress(customerId);
  const deleteAddr = useDeleteCustomerAddress(customerId);
  const setAddressFreeze = useSetCustomerAddressFreeze(customerId);

  const rows = data?.addresses ?? [];

  const EMPTY_WAREHOUSES: CustomerAddressesResponse['warehouses'] = [];
  const warehouses = data?.warehouses ?? EMPTY_WAREHOUSES;

  const EMPTY_LOAD_OPTIONS: NonNullable<CustomerAddressesResponse['loadOptions']> = [];
  const loadOptions = data?.loadOptions ?? EMPTY_LOAD_OPTIONS;

  const EMPTY_PRODUCTS: CustomerAddressesResponse['products'] = [];
  const products = data?.products ?? EMPTY_PRODUCTS;

  const perItemPricing = data?.perItemPricing === true;
  const pricingEnabled = data?.pricingEnabled === true;

  // Customer-level "show pricing to customer" toggle — saves immediately (it's a
  // customer flag, not an address field), kept in sync with the fetched value.
  const setShowPricing = useSetCustomerShowPricing(customerId);
  const [showPricingToCustomer, setShowPricingToCustomer] = useState(false);
  useEffect(() => {
    setShowPricingToCustomer(Boolean(data?.showPricingToCustomer));
  }, [data?.showPricingToCustomer]);

  const customerTier = data?.customerTier ?? null;
  const hasCustomerTier = Boolean(customerTier?.id);

  const [mode, setMode] = useState<'list' | 'create' | 'edit'>('list');

  const [editingId, setEditingId] = useState<string | null>(null);

  const [form, setForm] = useState(createEmptyForm);

  // Per-sub-location sales rep (overrides the customer's). Kept out of the shared
  // address form module since it's a customer-org concern, not an address field.
  const { data: orgMembers = [] } = useListOrgMembers();
  const { data: me } = useCurrentUser();
  // Only the owner picks a location's rep (admins see it read-only and claim
  // via "Assign to me" on Orders), and not themselves — hidden unless already
  // selected, so a save doesn't clear it.
  const canEditSalesRep = me?.type === 'ORG_OWNER';
  const hiddenSalesRepUid = canEditSalesRep ? me.firebaseUid : null;
  const assignToMe = useAssignCustomerToSalesRep(customerId);
  const [salesRepUserId, setSalesRepUserId] = useState<string>('');

  const [applyCustomerTierDiscount, setApplyCustomerTierDiscount] = useState(false);

  const [productAccess, setProductAccess] = useState<ProductAccessMap>({});

  /*
   * productId -> one-shot "apply this access state to every location" flag.
   * Nothing is loaded into this map from the server, so every checkbox always
   * starts unchecked until an admin explicitly checks it.
   */
  const [applyProductAccessToAllLocations, setApplyProductAccessToAllLocations] =
    useState<ProductApplyToAllMap>({});

  const [productPrice, setProductPrice] = useState<ProductPriceMap>({});
  const [productSearch, setProductSearch] = useState('');
  const [showConfiguredProductsOnly, setShowConfiguredProductsOnly] = useState(false);

  // Per-sub-location pricing region (auto-suggested from the address state).
  const [region, setRegion] = useState<'' | PricingRegion>('');

  const [warehouseSearch, setWarehouseSearch] = useState('');
  const [showConfiguredOnly, setShowConfiguredOnly] = useState(false);

  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [freezingId, setFreezingId] = useState<string | null>(null);

  const [loadOptionSearch, setLoadOptionSearch] = useState('');

  /*
   * Signature of the last loaded/saved editor state.
   * Search/filter controls are intentionally excluded.
   */
  const [savedDraftSignature, setSavedDraftSignature] = useState('');

  const tableWrapRef = useRef<HTMLDivElement | null>(null);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);

  useEffect(() => {
    const el = tableWrapRef.current;
    if (!el) return;

    const updateScrollState = () => {
      const maxScrollLeft = el.scrollWidth - el.clientWidth;

      setCanScrollLeft(el.scrollLeft > 4);
      setCanScrollRight(el.scrollLeft < maxScrollLeft - 4);
    };

    updateScrollState();
    el.addEventListener('scroll', updateScrollState);
    window.addEventListener('resize', updateScrollState);

    return () => {
      el.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, []);

  useEffect(() => {
    if (!customerId) {
      return;
    }

    setMode('list');
    setEditingId(null);

    setForm(createEmptyForm());
    setSalesRepUserId('');
    setApplyCustomerTierDiscount(false);

    setProductAccess({});
    setApplyProductAccessToAllLocations({});
    setProductSearch('');
    setShowConfiguredProductsOnly(false);

    setWarehouseSearch('');
    setLoadOptionSearch('');
    setShowConfiguredOnly(false);

    setDeletingId(null);
    setFreezingId(null);
    setSavedDraftSignature('');
  }, [customerId]);

  /*
   * Candidate warehouses are returned by the API.
   *
   * When per-item pricing is ON:
   * only warehouses containing an ENABLED product
   * for this form should be available for carrier rates.
   *
   * PREVIEW products do not contribute warehouses.
   *
   * When per-item pricing is OFF:
   * all candidate warehouses remain available because
   * products are available by default.
   */
  const availableWarehousesForForm = useMemo(() => {
    if (!perItemPricing) {
      return warehouses;
    }

    const allowedWarehouseIds = getAvailableWarehouseIdsForProducts(products, productAccess);

    return warehouses.filter((warehouse) => allowedWarehouseIds.has(warehouse.id));
  }, [warehouses, products, productAccess, perItemPricing]);

  const filteredWarehouses = useMemo(
    () => filterWarehouses(availableWarehousesForForm, form, warehouseSearch, showConfiguredOnly),
    [availableWarehousesForForm, warehouseSearch, showConfiguredOnly, form]
  );

  const filteredLoadOptions = useMemo(
    () => sortAndFilterLoadOptions(loadOptions, form, loadOptionSearch),
    [loadOptions, loadOptionSearch, form]
  );

  const filteredProducts = useMemo(() => {
    const normalizedSearch = productSearch.trim().toLowerCase();

    return products
      .filter((product) => {
        const access = getProductAccess(productAccess, product.id);

        if (showConfiguredProductsOnly && access === 'DISABLED') {
          return false;
        }

        if (!normalizedSearch) {
          return true;
        }

        return [product.name, product.deliveryType]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
          .includes(normalizedSearch);
      })
      .sort((a, b) => {
        const accessA = getProductAccess(productAccess, a.id);
        const accessB = getProductAccess(productAccess, b.id);

        const rank = (access: ProductAccessState) => {
          if (access === 'ENABLED') return 0;
          if (access === 'PREVIEW') return 1;
          return 2;
        };

        const accessCompare = rank(accessA) - rank(accessB);

        if (accessCompare !== 0) {
          return accessCompare;
        }

        return String(a.name ?? '').localeCompare(String(b.name ?? ''));
      });
  }, [products, productAccess, productSearch, showConfiguredProductsOnly]);

  const configuredProductCount = useMemo(
    () =>
      Object.values(productAccess).filter((access) => access === 'ENABLED' || access === 'PREVIEW')
        .length,
    [productAccess]
  );

  const enabledProductCount = useMemo(
    () => Object.values(productAccess).filter((access) => access === 'ENABLED').length,
    [productAccess]
  );

  const previewProductCount = useMemo(
    () => Object.values(productAccess).filter((access) => access === 'PREVIEW').length,
    [productAccess]
  );

  const configuredFormRateCount = useMemo(() => countConfiguredRates(form), [form]);

  const toggleLoadOption = (loadOptionId: string, checked: boolean) => {
    setForm((current) => applyToggleLoadOption(current, loadOptionId, checked));
  };

  const currentDraftSignature = useMemo(
    () =>
      buildEditorSignature({
        form,
        salesRepUserId,
        applyCustomerTierDiscount,
        productAccess,
        applyProductAccessToAllLocations,
        region,
        productPrice,
      }),
    [
      form,
      salesRepUserId,
      applyCustomerTierDiscount,
      productAccess,
      applyProductAccessToAllLocations,
      region,
      productPrice,
    ]
  );

  const hasUnsavedChanges =
    mode !== 'list' && savedDraftSignature !== '' && currentDraftSignature !== savedDraftSignature;

  const resetForm = () => {
    setMode('list');
    setEditingId(null);

    setForm(createEmptyForm());
    setSalesRepUserId('');
    setApplyCustomerTierDiscount(false);

    setProductAccess({});
    setApplyProductAccessToAllLocations({});
    setProductPrice({});
    setRegion('');
    setProductSearch('');
    setShowConfiguredProductsOnly(false);

    setWarehouseSearch('');
    setLoadOptionSearch('');
    setShowConfiguredOnly(false);
    setSavedDraftSignature('');
  };

  const beginCreate = () => {
    const nextForm = createEmptyForm();
    const nextProductAccess: ProductAccessMap = {};

    setMode('create');
    setEditingId(null);

    setForm(nextForm);
    setSalesRepUserId('');
    setApplyCustomerTierDiscount(false);

    setProductAccess(nextProductAccess);
    setApplyProductAccessToAllLocations({});
    setProductPrice({});
    setRegion('');
    setProductSearch('');
    setShowConfiguredProductsOnly(false);

    setWarehouseSearch('');
    setLoadOptionSearch('');
    setShowConfiguredOnly(false);

    setSavedDraftSignature(
      buildEditorSignature({
        form: nextForm,
        salesRepUserId: '',
        applyCustomerTierDiscount: false,
        productAccess: nextProductAccess,
        applyProductAccessToAllLocations: {},
        region: '',
        productPrice: {},
      })
    );
  };

  // Admins claim an unassigned location for themselves (the server refuses if
  // someone got there first). It saves straight away, so move the saved
  // baseline too — otherwise the editor would show the rep as unsaved.
  const onAssignLocationToMe = () => {
    if (!editingId || !me?.firebaseUid) return;

    const uid = me.firebaseUid;

    assignToMe.mutate(editingId, {
      onSuccess: () => {
        setSalesRepUserId(uid);
        setSavedDraftSignature((previous) => {
          if (!previous) return previous;
          const saved = JSON.parse(previous);
          saved.salesRepUserId = uid;
          return JSON.stringify(saved);
        });
        toast.success('You are now the sales rep for this location.');
      },
      onError: (error: any) => toast.error(error?.response?.data?.error || 'Assign failed'),
    });
  };

  const beginEdit = (row: any) => {
    const nextForm = buildFormFromAddress(row);
    const nextSalesRepUserId = row.salesRepUserId ?? '';
    const nextApplyCustomerTierDiscount = hasCustomerTier && row.applyCustomerTierDiscount === true;
    const nextProductAccess = buildProductAccessFromAddress(row);
    const nextProductPrice = buildProductPriceFromAddress(row);
    const nextRegion: '' | PricingRegion = (row.region as PricingRegion | null) ?? '';

    setMode('edit');
    setEditingId(row.id);

    setForm(nextForm);
    setSalesRepUserId(nextSalesRepUserId);
    setApplyCustomerTierDiscount(nextApplyCustomerTierDiscount);

    setProductAccess(nextProductAccess);
    setApplyProductAccessToAllLocations({});
    setProductPrice(nextProductPrice);
    setRegion(nextRegion);

    setProductSearch('');
    setShowConfiguredProductsOnly(false);

    setWarehouseSearch('');
    setLoadOptionSearch('');
    setShowConfiguredOnly(false);

    setSavedDraftSignature(
      buildEditorSignature({
        form: nextForm,
        salesRepUserId: nextSalesRepUserId,
        applyCustomerTierDiscount: nextApplyCustomerTierDiscount,
        productAccess: nextProductAccess,
        applyProductAccessToAllLocations: {},
        region: nextRegion,
        productPrice: nextProductPrice,
      })
    );
  };

  const updateProductAccess = (productId: string, nextAccess: ProductAccessState) => {
    setProductAccess((currentAccess) => {
      const nextProductAccess = {
        ...currentAccess,
        [productId]: nextAccess,
      };

      if (nextAccess === 'DISABLED') {
        delete nextProductAccess[productId];
      }

      /*
       * Product changes can make some previously
       * configured carrier-rate warehouses invalid.
       *
       * Automatically remove carrier-rate values for
       * warehouses no longer reachable through any
       * ENABLED product.
       */
      if (perItemPricing) {
        const allowedWarehouseIds = getAvailableWarehouseIdsForProducts(
          products,
          nextProductAccess
        );

        setForm((currentForm) => {
          const currentCarrierRates = currentForm.carrierRates ?? {};

          const nextCarrierRates = Object.fromEntries(
            Object.entries(currentCarrierRates).filter(([warehouseId]) =>
              allowedWarehouseIds.has(warehouseId)
            )
          );

          return {
            ...currentForm,
            carrierRates: nextCarrierRates,
          };
        });
      }

      return nextProductAccess;
    });
  };

  const setProductApplyToAllLocations = (productId: string, checked: boolean) => {
    setApplyProductAccessToAllLocations((current) => {
      const next = { ...current };

      if (checked) {
        next[productId] = true;
      } else {
        delete next[productId];
      }

      return next;
    });
  };

  const updateCarrierRate = (warehouseId: string, value: string) => {
    setForm((current) => applyCarrierRate(current, warehouseId, value));
  };

  const clearCarrierRate = (warehouseId: string) => {
    setForm((current) => applyClearCarrierRate(current, warehouseId));
  };

  const validate = () => validateAddressForm(form, availableWarehousesForForm);

  const onSave = async () => {
    const validationError = validate();

    if (validationError) {
      toast.warn(validationError);

      return;
    }

    const basePayload = buildAddressPayload(form);

    /*
     * Product access is sent as part of the same
     * location save operation.
     *
     * When per-item pricing is disabled we omit
     * products entirely so PATCH preserves any
     * existing data rather than replacing it.
     */
    // Only submit assignments for products that still exist. An address can
    // carry assignments for products deleted after they were configured; those
    // are hidden in the UI but were still being sent, and the server rejects
    // them (INVALID_PRODUCT), blocking every save. Dropping them here also
    // self-heals the stale rows on the next save.
    const availableProductIds = new Set(products.map((product) => product.id));
    const productsPayload = buildProductsPayload(productAccess, productPrice).filter((entry) =>
      availableProductIds.has(entry.productId)
    );

    /*
     * One-shot bulk propagation instructions.
     *
     * We send product IDs only. The API derives ENABLED / PREVIEW / DISABLED
     * from this save's product state. Delivered price is intentionally not
     * propagated to the customer's other locations.
     */
    const applyProductAccessToAllLocationIds = products
      .filter((product) => applyProductAccessToAllLocations[product.id] === true)
      .map((product) => product.id);

    const payload = {
      ...basePayload,

      salesRepUserId: salesRepUserId || null,

      // If the customer no longer has a tier, send false so a normal
      // address save cannot leave an active discount flag behind.
      applyCustomerTierDiscount: hasCustomerTier && applyCustomerTierDiscount,

      ...(pricingEnabled ? { region: region || null } : {}),

      ...(perItemPricing
        ? {
            products: productsPayload,

            ...(applyProductAccessToAllLocationIds.length > 0
              ? {
                  applyProductAccessToAllLocations: applyProductAccessToAllLocationIds,
                }
              : {}),
          }
        : {}),
    };

    try {
      if (mode === 'create') {
        const result = await createAddr.mutateAsync(payload);

        if (result?.address?.id) {
          setMode('edit');
          setEditingId(result.address.id);
        }

        toast.success('Address added.');

        /*
         * Stay in the editor after creating so the new location's products,
         * fulfilment methods, and carrier rates can be configured. The saved
         * values become the clean baseline, so Save disables until an edit.
         */
        /*
         * Apply-to-all is a one-shot action. After a successful save, clear
         * every checkbox and make that unchecked state the new clean baseline.
         */
        setApplyProductAccessToAllLocations({});

        setSavedDraftSignature(
          buildEditorSignature({
            form,
            salesRepUserId,
            applyCustomerTierDiscount,
            productAccess,
            applyProductAccessToAllLocations: {},
            region,
            productPrice,
          })
        );
      } else {
        await updateAddr.mutateAsync({
          addressId: editingId as string,

          data: payload,
        });

        toast.success('Address updated.');

        // Saving an edit returns to the addresses list.
        resetForm();
      }
    } catch (error) {
      const errorCode = (error as any)?.response?.data?.error;

      if (errorCode === 'CUSTOMER_TIER_REQUIRED_FOR_LOCATION_DISCOUNT') {
        toast.error(
          'This customer no longer has a tier assigned. Refresh the customer and try again.'
        );
      } else if (errorCode === 'INVALID_CUSTOMER_TIER_DISCOUNT_FLAG') {
        toast.error('Invalid customer tier discount setting.');
      } else {
        toast.error(saveErrorMessage(errorCode));
      }
    }
  };

  const onDelete = async (row: any) => {
    if (!window.confirm(`Remove address "${row.label || row.line1}"?`)) {
      return;
    }

    try {
      setDeletingId(row.id);

      await deleteAddr.mutateAsync(row.id);

      toast.success('Address removed.');
    } catch (error) {
      toast.error(deleteErrorMessage((error as any)?.response?.data?.error));
    } finally {
      setDeletingId(null);
    }
  };

  const onToggleFreeze = async (row: any) => {
    const nextOnFreeze = row.onFreeze !== true;

    if (
      nextOnFreeze &&
      !window.confirm(
        `Freeze "${row.label || row.line1}" for ordering?\n\nCustomers will still see the location, but new orders to it will be blocked until it is unfrozen.`
      )
    ) {
      return;
    }

    try {
      setFreezingId(row.id);

      await setAddressFreeze.mutateAsync({
        addressId: row.id,
        onFreeze: nextOnFreeze,
      });

      toast.success(nextOnFreeze ? 'Location frozen for ordering.' : 'Location unfrozen.');
    } catch (error) {
      const errorCode = (error as any)?.response?.data?.error;

      toast.error(
        errorCode === 'INVALID_FREEZE_FLAG'
          ? 'Invalid freeze setting.'
          : 'Could not update the location ordering status.'
      );
    } finally {
      setFreezingId(null);
    }
  };

  const isSaving = createAddr.isPending || updateAddr.isPending;

  const confirmDiscardChanges = () => {
    if (!hasUnsavedChanges) {
      return true;
    }

    return window.confirm('You have unsaved changes to this location. Discard them and exit?');
  };

  const handleClose = () => {
    if (confirmDiscardChanges()) {
      onClose();
    }
  };

  const handleCancelEdit = () => {
    if (confirmDiscardChanges()) {
      resetForm();
    }
  };

  useEffect(() => {
    if (!hasUnsavedChanges) {
      return;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  return (
    <div
      className={`modal customer-addresses-modal customer-addresses-modal--${mode}`}
      role="dialog"
      aria-modal="true"
    >
      <div className="modal__backdrop" onClick={handleClose} />

      <div className="modal__panel">
        <div className="modal__header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {mode !== 'list' ? (
              <button type="button" className="btn btn--ghost" onClick={handleCancelEdit}>
                ← Addresses
              </button>
            ) : onBack ? (
              <button type="button" className="btn btn--ghost" onClick={onBack}>
                ← Back to customer
              </button>
            ) : null}

            <div>
              <h3 className="modal__title">Customer Addresses</h3>

              {mode !== 'list' && (
                <div className="muted tiny">
                  {mode === 'create'
                    ? 'Add a customer delivery location and configure its products, fulfilment methods, and carrier rates.'
                    : 'Update the delivery location and its products, fulfilment methods, and carrier rates.'}
                </div>
              )}
            </div>
          </div>

          <button type="button" className="btn btn--ghost" onClick={handleClose}>
            Close
          </button>
        </div>

        <div className="modal__body">
          <div className="flex-between customer-addresses-modal__toolbar">
            <div className="muted">
              {rows.length} address
              {rows.length === 1 ? '' : 'es'}
            </div>

            {mode === 'list' && (
              <button type="button" className="btn btn--primary" onClick={beginCreate}>
                Add Address
              </button>
            )}
          </div>

          {isLoading ? (
            <div
              className="skeleton"
              style={{
                height: 160,
              }}
            />
          ) : mode === 'list' ? (
            rows.length === 0 ? (
              <div className="muted">No addresses yet.</div>
            ) : (
              <div
                ref={tableWrapRef}
                className={[
                  'customer-addresses-table-wrap',
                  canScrollLeft ? 'customer-addresses-table-wrap--can-scroll-left' : '',
                  canScrollRight ? 'customer-addresses-table-wrap--can-scroll-right' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                {canScrollRight && (
                  <div className="customer-addresses-table-wrap__scroll-hint">
                    Scroll right to view more →
                  </div>
                )}

                <table className="table customer-addresses-table">
                  <thead>
                    <tr>
                      <th className="customer-addresses-table__location">Location Name</th>

                      <th>Ship To</th>

                      <th>Subusers</th>

                      <th>Address</th>

                      {hasCustomerTier && <th>Tier Discount</th>}

                      {perItemPricing && <th>Products</th>}

                      <th>Fulfilment Methods</th>

                      <th className="ta-right">Delivery Rates</th>

                      <th>Primary</th>

                      <th>Headquarters</th>

                      <th className="customer-addresses-table__ordering">Ordering</th>

                      <th className="ta-right customer-addresses-table__actions">Actions</th>
                    </tr>
                  </thead>

                  <tbody>
                    {rows.map((row: any) => {
                      const configuredCount = getConfiguredRateCount(row);

                      const availableCount = Number.isFinite(Number(row.availableWarehouseCount))
                        ? Number(row.availableWarehouseCount)
                        : Array.isArray(row.warehouses)
                          ? row.warehouses.length
                          : 0;

                      const isDeleting = deletingId === row.id;
                      const isFreezing = freezingId === row.id;

                      const customerUsers = row.customerUsers ?? [];

                      const visibleCustomerUsers = customerUsers.slice(0, 2);

                      const remainingCustomerUserCount = Math.max(customerUsers.length - 2, 0);

                      const assignedProducts: LocationProductAssignment[] = Array.isArray(
                        row.products
                      )
                        ? row.products
                        : [];

                      const enabledProducts = assignedProducts.filter(
                        (product) => product.isEnabled
                      );

                      const previewProducts = assignedProducts.filter(
                        (product) => !product.isEnabled && product.isPreview
                      );

                      const configuredProducts = [...enabledProducts, ...previewProducts];

                      const assignedLoadOptions = row.loadOptions ?? [];

                      const sortedAssignedLoadOptions = [...assignedLoadOptions].sort((a, b) => {
                        const aIsDefault =
                          a.id === row.defaultFulfillmentMethodId ||
                          a.orgLoadOptionId === row.defaultFulfillmentMethodId;

                        const bIsDefault =
                          b.id === row.defaultFulfillmentMethodId ||
                          b.orgLoadOptionId === row.defaultFulfillmentMethodId;

                        if (aIsDefault && !bIsDefault) {
                          return -1;
                        }

                        if (!aIsDefault && bIsDefault) {
                          return 1;
                        }

                        return String(a.name ?? '').localeCompare(String(b.name ?? ''));
                      });

                      const visibleLoadOptions = sortedAssignedLoadOptions.slice(0, 2);

                      const remainingLoadOptionCount = Math.max(
                        sortedAssignedLoadOptions.length - visibleLoadOptions.length,
                        0
                      );

                      return (
                        <tr key={row.id}>
                          <td className="customer-addresses-table__location">
                            {row.label ?? <span className="muted">—</span>}
                          </td>

                          <td>
                            <div
                              style={{
                                fontWeight: 600,
                              }}
                            >
                              {row.fullName}
                            </div>

                            <div className="muted tiny">{formatPhone(row.phone)}</div>
                          </td>

                          <td>
                            {customerUsers.length === 0 ? (
                              <span className="muted">—</span>
                            ) : (
                              <div className="customer-address-users">
                                {visibleCustomerUsers.map((customerUser: any) => (
                                  <div
                                    key={customerUser.id}
                                    className="customer-address-users__name"
                                    title={customerUser.email}
                                  >
                                    {getCustomerUserDisplayName(customerUser)}
                                  </div>
                                ))}

                                {remainingCustomerUserCount > 0 && (
                                  <div className="muted tiny">
                                    +{remainingCustomerUserCount} more
                                  </div>
                                )}
                              </div>
                            )}
                          </td>

                          <td>
                            <div>
                              {row.line1}

                              {row.line2 ? `, ${row.line2}` : ''}
                            </div>

                            <div className="muted tiny">
                              {row.city}, {row.state} {row.postalCode} · {row.country}
                            </div>
                          </td>

                          {hasCustomerTier && (
                            <td>
                              {row.applyCustomerTierDiscount ? (
                                <span className="customer-address-tier-status is-enabled">Yes</span>
                              ) : (
                                <span className="muted">No</span>
                              )}
                            </td>
                          )}

                          {perItemPricing && (
                            <td>
                              {configuredProducts.length === 0 ? (
                                <span className="muted">—</span>
                              ) : (
                                <div className="customer-location-products-summary">
                                  <div className="muted tiny" style={{ whiteSpace: 'nowrap' }}>
                                    {enabledProducts.length} enabled
                                    {previewProducts.length > 0
                                      ? ` · ${previewProducts.length} preview`
                                      : ''}
                                  </div>
                                </div>
                              )}
                            </td>
                          )}

                          <td>
                            {assignedLoadOptions.length === 0 ? (
                              <span className="muted">—</span>
                            ) : (
                              <div className="customer-location-load-options-summary">
                                {visibleLoadOptions.map((option: any) => (
                                  <div
                                    key={option.orgLoadOptionId ?? option.id}
                                    className="customer-location-load-options-summary__item"
                                    title={option.name}
                                  >
                                    {option.name}

                                    {option.capacityLabel ? ` (${option.capacityLabel}t)` : ''}

                                    {(option.id === row.defaultFulfillmentMethodId ||
                                      option.orgLoadOptionId ===
                                        row.defaultFulfillmentMethodId) && (
                                      <span className="customer-location-load-options-summary__default">
                                        Default
                                      </span>
                                    )}
                                  </div>
                                ))}

                                {remainingLoadOptionCount > 0 && (
                                  <div className="muted tiny">+{remainingLoadOptionCount} more</div>
                                )}
                              </div>
                            )}
                          </td>

                          <td className="ta-right">
                            <div
                              style={{
                                fontWeight: 600,
                              }}
                            >
                              {configuredCount} of {availableCount}
                            </div>

                            <div className="muted tiny">configured</div>
                          </td>

                          <td>{row.isPrimary ? 'Yes' : 'No'}</td>

                          <td>{row.isHeadquarters ? 'Yes' : 'No'}</td>

                          <td className="customer-addresses-table__ordering">
                            {row.onFreeze ? (
                              <span className="customer-address-ordering-status is-frozen">
                                Frozen
                              </span>
                            ) : (
                              <span className="customer-address-ordering-status is-open">Open</span>
                            )}
                          </td>

                          <td className="ta-right customer-addresses-table__actions">
                            <div className="customer-addresses-table__action-buttons">
                              <button
                                type="button"
                                className="btn btn--ghost"
                                onClick={() => beginEdit(row)}
                                disabled={isDeleting}
                              >
                                Edit
                              </button>

                              <button
                                type="button"
                                className={`btn btn--ghost customer-address-freeze-button ${
                                  row.onFreeze ? 'is-unfreeze' : 'is-freeze'
                                }`}
                                onClick={() => onToggleFreeze(row)}
                                disabled={isDeleting || isFreezing}
                              >
                                {isFreezing ? 'Updating…' : row.onFreeze ? 'Unfreeze' : 'Freeze'}
                              </button>

                              <button
                                type="button"
                                className="btn btn--ghost"
                                onClick={() => onDelete(row)}
                                disabled={isDeleting || isFreezing}
                              >
                                {isDeleting ? 'Removing…' : 'Remove'}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )
          ) : (
            <>
              {/* ADDRESS DETAILS */}
              <div
                className="grid"
                style={{
                  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',

                  gap: 12,
                }}
              >
                <div className="form-control">
                  <label>Label</label>

                  <input
                    value={form.label}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        label: event.target.value,
                      }))
                    }
                    placeholder="Warehouse / Job Site / etc"
                  />
                </div>

                <div className="form-control">
                  <label>Full Name *</label>

                  <input
                    value={form.fullName}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,

                        fullName: event.target.value,
                      }))
                    }
                  />
                </div>

                <div className="form-control">
                  <label>Phone *</label>

                  <input
                    value={form.phone}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,

                        phone: event.target.value,
                      }))
                    }
                  />
                </div>

                <div
                  className="form-control"
                  style={{
                    gridColumn: '1 / -1',
                  }}
                >
                  <label>Address line 1 *</label>

                  <AddressAutocomplete
                    value={form.line1}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,

                        line1: event.target.value,
                      }))
                    }
                    onPlaceSelected={(fields) =>
                      setForm((current) => ({
                        ...current,

                        line1: fields.line1,

                        city: fields.city,

                        state: fields.state,

                        postalCode: fields.postalCode,

                        country: fields.country,

                        // Google returns the unit as subpremise; fill it when present.
                        // A fresh pick is authoritative — set the unit or clear a stale one.
                        line2: fields.line2,
                      }))
                    }
                  />
                </div>

                <div
                  className="form-control"
                  style={{
                    gridColumn: '1 / -1',
                  }}
                >
                  <label>Address line 2</label>

                  <input
                    value={form.line2}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,

                        line2: event.target.value,
                      }))
                    }
                    placeholder="Suite, building, unit, etc."
                  />
                </div>

                <div className="form-control">
                  <label>City *</label>

                  <input
                    value={form.city}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,

                        city: event.target.value,
                      }))
                    }
                  />
                </div>

                <div className="form-control">
                  <label>State *</label>

                  <input
                    value={form.state}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,

                        state: event.target.value,
                      }))
                    }
                  />
                </div>

                <div className="form-control">
                  <label>Postal Code *</label>

                  <input
                    value={form.postalCode}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,

                        postalCode: event.target.value,
                      }))
                    }
                  />
                </div>

                <div className="form-control">
                  <label>Country *</label>

                  <input
                    value={form.country}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,

                        country: event.target.value,
                      }))
                    }
                  />
                </div>

                <div className="form-control" style={{ gridColumn: '1 / -1' }}>
                  <label>Sales Rep</label>
                  <select
                    value={salesRepUserId}
                    onChange={(event) => setSalesRepUserId(event.target.value)}
                    disabled={!canEditSalesRep}
                  >
                    <option value="">No sales rep (use customer default)</option>
                    {orgMembers
                      .filter((member: any) => member.user?.firebaseUid)
                      .filter(
                        (member: any) =>
                          member.user.firebaseUid !== hiddenSalesRepUid ||
                          member.user.firebaseUid === salesRepUserId
                      )
                      .map((member: any) => (
                        <option key={member.user.firebaseUid} value={member.user.firebaseUid}>
                          {member.user.name || member.user.email}
                        </option>
                      ))}
                  </select>
                  {me?.type === 'ORG_ADMIN' && mode === 'edit' && editingId && !salesRepUserId && (
                    <button
                      type="button"
                      className="btn btn--secondary"
                      style={{ marginTop: 8, alignSelf: 'flex-start' }}
                      onClick={onAssignLocationToMe}
                      disabled={assignToMe.isPending}
                    >
                      Assign to me
                    </button>
                  )}
                </div>

                {customerTier && (
                  <div className="form-control customer-address-tier-discount">
                    <label className="customer-address-tier-discount__toggle">
                      <input
                        type="checkbox"
                        checked={applyCustomerTierDiscount}
                        onChange={(event) => setApplyCustomerTierDiscount(event.target.checked)}
                      />

                      <span className="customer-address-tier-discount__content">
                        <span className="customer-address-tier-discount__title">
                          Apply customer tier discount at this location
                        </span>

                        <span className="customer-address-tier-discount__tier">
                          <svg
                            className="customer-address-tier-discount__swatch"
                            viewBox="0 0 12 12"
                            aria-hidden="true"
                          >
                            <circle cx="6" cy="6" r="6" fill={customerTier.badgeColor} />
                          </svg>

                          <span>
                            {customerTier.name} · ${customerTier.discountAmountCents / 100} discount
                          </span>
                        </span>

                        <span className="muted tiny">
                          When enabled, the customer's tier discount is applied to eligible product
                          pricing for orders shipped to this address.
                        </span>
                      </span>
                    </label>
                  </div>
                )}

                <div
                  className="form-control"
                  style={{
                    gridColumn: '1 / -1',
                  }}
                >
                  <label
                    style={{
                      display: 'flex',

                      alignItems: 'center',

                      gap: 8,

                      cursor: 'pointer',

                      fontWeight: 500,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={form.isPrimary}
                      onChange={(event) =>
                        setForm((current) => ({
                          ...current,

                          isPrimary: event.target.checked,
                        }))
                      }
                      style={{
                        width: 16,
                        height: 16,
                      }}
                    />
                    Primary address
                  </label>

                  <div className="muted tiny">
                    If selected, this will become the customer’s primary address.
                  </div>
                </div>

                <div
                  className="form-control"
                  style={{
                    gridColumn: '1 / -1',
                  }}
                >
                  <label
                    style={{
                      display: 'flex',

                      alignItems: 'center',

                      gap: 8,

                      cursor: 'pointer',

                      fontWeight: 500,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={form.isHeadquarters}
                      onChange={(event) =>
                        setForm((current) => ({
                          ...current,

                          isHeadquarters: event.target.checked,
                        }))
                      }
                      style={{
                        width: 16,
                        height: 16,
                      }}
                    />
                    Headquarters address
                  </label>
                </div>

                <div
                  className="form-control"
                  style={{
                    gridColumn: '1 / -1',
                  }}
                >
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      cursor: 'pointer',
                      fontWeight: 500,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={form.requiresPoNumber}
                      onChange={(event) =>
                        setForm((current) => ({
                          ...current,
                          requiresPoNumber: event.target.checked,
                        }))
                      }
                      style={{
                        width: 16,
                        height: 16,
                      }}
                    />
                    Require a PO number for orders to this location
                  </label>
                  <div className="muted tiny" style={{ marginLeft: 24 }}>
                    Customers must enter a PO number when ordering to this sub-location.
                  </div>
                </div>
              </div>

              {/* RECEIVING HOURS */}
              <OperatingHoursEditor
                value={form.operatingHours}
                onChange={(operatingHours) =>
                  setForm((current) => ({ ...current, operatingHours }))
                }
              />

              {/* DELIVERY NOTES */}
              <div className="form-control" style={{ marginTop: 8 }}>
                <label>Delivery notes</label>
                <textarea
                  rows={3}
                  value={form.deliveryNotes}
                  onChange={(e) =>
                    setForm((current) => ({ ...current, deliveryNotes: e.target.value }))
                  }
                  placeholder="Gate hours, truck access, where to dump…"
                />
              </div>

              {/* PRICING */}
              {pricingEnabled && (
                <>
                  <div className="form-control" style={{ marginTop: 8 }}>
                    <label>Show pricing</label>

                    <label
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 10,
                        cursor: 'pointer',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={showPricingToCustomer}
                        disabled={setShowPricing.isPending}
                        onChange={(event) => {
                          const next = event.target.checked;
                          setShowPricingToCustomer(next);
                          setShowPricing.mutate(next, {
                            onSuccess: () => {
                              toast.success(
                                next
                                  ? 'Update complete — pricing is now shown to this customer.'
                                  : 'Update complete — pricing is now hidden from this customer.'
                              );
                            },
                            onError: () => {
                              setShowPricingToCustomer(!next);
                              toast.error('Failed to update pricing visibility.');
                            },
                          });
                        }}
                        style={{ width: 16, height: 16, marginTop: 2 }}
                      />

                      <span>
                        <strong>Show pricing to this customer</strong>

                        <span className="muted tiny" style={{ display: 'block', marginTop: 2 }}>
                          When off, prices and order totals stay internal (admin-only). Applies to
                          the whole customer. Turn on once their prices are verified.
                        </span>
                      </span>
                    </label>
                  </div>

                  <div className="form-control" style={{ marginTop: 8 }}>
                    <label>Pricing region</label>

                    <select
                      value={region}
                      onChange={(event) => setRegion(event.target.value as '' | PricingRegion)}
                    >
                      <option value="">Not set</option>
                      {REGION_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>

                    <div className="muted tiny">
                      {region === 'NORTHEAST' &&
                        'One all-in delivered price per product/ton — set it on each product below.'}
                      {region === 'SOUTHERN' &&
                        'Stone price (on the product) + this location’s delivery rate; tier discount on stone.'}
                      {region === 'OTHER' &&
                        'No standard pricing structure — orders show “contact for pricing”.'}
                      {region === '' &&
                        (() => {
                          const suggested = resolveRegion(form.state);
                          return suggested !== 'OTHER' ? (
                            <>
                              Suggested from state:{' '}
                              <button
                                type="button"
                                onClick={() => setRegion(suggested)}
                                style={{
                                  padding: 0,
                                  border: 'none',
                                  background: 'none',
                                  font: 'inherit',
                                  color: '#2563eb',
                                  cursor: 'pointer',
                                  textDecoration: 'underline',
                                }}
                              >
                                {REGION_LABELS[suggested]}
                              </button>
                            </>
                          ) : (
                            'Set the region so loads to this location price correctly.'
                          );
                        })()}
                    </div>
                  </div>
                </>
              )}

              {/* PRODUCTS */}
              {perItemPricing && (
                <div className="customer-location-products">
                  <div className="customer-location-products__header">
                    <div className="customer-location-products__heading">
                      <div className="customer-location-products__title">Products</div>

                      <div className="muted tiny">
                        Configure product access specifically for this delivery location. Enabled
                        products can be ordered. Preview products are visible but cannot be
                        purchased. Check <strong>Apply to all locations</strong> on a product to
                        copy only its access state to every existing delivery location when you
                        save.
                      </div>
                    </div>

                    <div className="muted tiny customer-location-products__count">
                      {configuredProductCount} of {products.length} configured
                    </div>
                  </div>

                  {products.length === 0 ? (
                    <div className="customer-location-products__empty muted">
                      No active products are available for this organization.
                    </div>
                  ) : (
                    <>
                      <div className="customer-location-products__toolbar">
                        <div className="customer-location-products__search">
                          <SearchInput
                            className="customer-location-products__search-input"
                            value={productSearch}
                            onValueChange={setProductSearch}
                            placeholder="Search products..."
                            aria-label="Search products"
                          />
                        </div>

                        <label className="customer-location-products__configured-toggle">
                          <input
                            type="checkbox"
                            checked={showConfiguredProductsOnly}
                            onChange={(event) =>
                              setShowConfiguredProductsOnly(event.target.checked)
                            }
                          />

                          <span>Configured only</span>
                        </label>
                      </div>

                      <div className="customer-location-products__list">
                        {filteredProducts.length === 0 ? (
                          <div className="customer-location-products__no-results muted">
                            No products match the current filters.
                          </div>
                        ) : (
                          filteredProducts.map((product) => {
                            const access = getProductAccess(productAccess, product.id);

                            const accessClass =
                              access === 'ENABLED'
                                ? 'is-enabled'
                                : access === 'PREVIEW'
                                  ? 'is-preview'
                                  : 'is-disabled';

                            return (
                              <div
                                key={product.id}
                                className={`customer-location-products__row ${accessClass}`}
                              >
                                <div className="customer-location-products__product">
                                  <div className="customer-location-products__image">
                                    <img
                                      src={product.imageUrl?.trim() || fallbackProductImage}
                                      alt={product.name || 'Product'}
                                      onError={(e) => {
                                        e.currentTarget.src = fallbackProductImage;
                                      }}
                                    />
                                  </div>

                                  <div className="customer-location-products__details">
                                    <div className="customer-location-products__name">
                                      {product.name}
                                    </div>

                                    <div className="customer-location-products__meta muted tiny">
                                      {product.deliveryType ?? 'Product'}

                                      {Array.isArray(product.warehouseIds) &&
                                        ` · ${product.warehouseIds.length} warehouse${
                                          product.warehouseIds.length === 1 ? '' : 's'
                                        }`}
                                    </div>
                                  </div>
                                </div>

                                <div
                                  className={`customer-location-products__access ${accessClass}`}
                                >
                                  {pricingEnabled && region === 'NORTHEAST' && (
                                    <div
                                      className="form-control customer-location-products__price"
                                      title="Delivered price per ton"
                                    >
                                      <input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        placeholder="$/ton"
                                        value={productPrice[product.id] ?? ''}
                                        onChange={(event) =>
                                          setProductPrice((current) => ({
                                            ...current,
                                            [product.id]: event.target.value,
                                          }))
                                        }
                                      />
                                    </div>
                                  )}

                                  <div className="customer-location-products__access-controls">
                                    <div className="customer-location-products__select-wrap">
                                      <select
                                        className="customer-location-products__select"
                                        value={access}
                                        onChange={(event) =>
                                          updateProductAccess(
                                            product.id,
                                            event.target.value as ProductAccessState
                                          )
                                        }
                                      >
                                        <option value="ENABLED">Enabled</option>

                                        <option value="PREVIEW">Preview</option>

                                        <option value="DISABLED">Disabled</option>
                                      </select>
                                    </div>

                                    <label
                                      className="customer-location-products__apply-all"
                                      title="On Save, apply this product's Enabled / Preview / Disabled state to every existing delivery location. Delivered prices on other locations are preserved."
                                    >
                                      <input
                                        type="checkbox"
                                        checked={
                                          applyProductAccessToAllLocations[product.id] === true
                                        }
                                        onChange={(event) =>
                                          setProductApplyToAllLocations(
                                            product.id,
                                            event.target.checked
                                          )
                                        }
                                      />

                                      <span>Apply to all locations</span>
                                    </label>
                                  </div>
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>

                      <div className="customer-location-products__summary">
                        <span className="customer-location-products__summary-item is-enabled">
                          {enabledProductCount} enabled
                        </span>

                        <span className="customer-location-products__summary-item is-preview">
                          {previewProductCount} preview
                        </span>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* FULFILMENT METHODS */}
              <div className="customer-location-load-options">
                <div className="customer-location-load-options__header">
                  <div>
                    <div className="customer-location-load-options__title">Fulfilment Methods</div>

                    <div className="muted tiny">
                      Select the methods available when this delivery location is chosen.
                    </div>
                  </div>

                  <div className="muted tiny customer-location-load-options__count">
                    {form.orgLoadOptionIds.length} of {loadOptions.length} selected
                  </div>
                </div>

                {loadOptions.length === 0 ? (
                  <div className="customer-location-load-options__empty muted">
                    No active fulfilment methods are available for this organization.
                  </div>
                ) : (
                  <>
                    <SearchInput
                      className="customer-location-load-options__search"
                      value={loadOptionSearch}
                      onValueChange={setLoadOptionSearch}
                      placeholder="Search fulfilment methods..."
                      aria-label="Search fulfilment methods"
                    />

                    <div className="customer-location-load-options__list">
                      {filteredLoadOptions.length === 0 ? (
                        <div
                          className="muted"
                          style={{
                            padding: 14,
                          }}
                        >
                          No fulfilment methods match your search.
                        </div>
                      ) : (
                        filteredLoadOptions.map((option: any) => {
                          // With no methods of its own, the location inherits the org
                          // default: show it checked and locked so the admin sees it
                          // applies, without forcing an explicit assignment.
                          const inheritsOrgDefault =
                            form.orgLoadOptionIds.length === 0 && Boolean(option.isDefault);

                          const checked =
                            form.orgLoadOptionIds.includes(option.id) || inheritsOrgDefault;

                          const isDefault = form.defaultFulfillmentMethodId === option.id;

                          return (
                            <label
                              key={option.id}
                              className={`customer-location-load-options__option ${
                                checked ? 'is-selected' : ''
                              }`}
                              style={{ cursor: inheritsOrgDefault ? 'default' : 'pointer' }}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                disabled={inheritsOrgDefault}
                                onChange={(event) =>
                                  toggleLoadOption(
                                    option.id,

                                    event.target.checked
                                  )
                                }
                              />

                              <span
                                className="customer-location-load-options__checkbox"
                                aria-hidden="true"
                              >
                                ✓
                              </span>

                              <span className="customer-location-load-options__details">
                                <div className="customer-location-load-options__header-row">
                                  <span className="customer-location-load-options__name">
                                    {option.name}
                                  </span>

                                  {inheritsOrgDefault ? (
                                    <span className="customer-location-load-options__default-badge">
                                      Organization default
                                    </span>
                                  ) : (
                                    isDefault && (
                                      <span className="customer-location-load-options__default-badge">
                                        Default
                                      </span>
                                    )
                                  )}
                                </div>

                                {option.capacityLabel && (
                                  <span className="muted tiny">
                                    Capacity: {option.capacityLabel} tons
                                  </span>
                                )}
                              </span>
                            </label>
                          );
                        })
                      )}
                    </div>
                  </>
                )}

                {form.orgLoadOptionIds.length === 0 &&
                  (() => {
                    const orgDefaultOption = loadOptions.find((option: any) => option.isDefault);

                    return orgDefaultOption ? (
                      <div className="customer-location-load-options__inherited muted tiny">
                        No methods selected — this location inherits the organization default:{' '}
                        <strong>{orgDefaultOption.name}</strong>.
                      </div>
                    ) : (
                      <div className="customer-location-load-options__required">
                        No fulfilment methods are selected and no organization default is
                        configured. Select at least one method.
                      </div>
                    );
                  })()}
              </div>

              {form.orgLoadOptionIds.length > 0 && (
                <div className="form-control customer-location-load-options__default">
                  <label>Default fulfilment method *</label>

                  <select
                    value={form.defaultFulfillmentMethodId}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,

                        defaultFulfillmentMethodId: event.target.value,
                      }))
                    }
                  >
                    <option value="">Select default method</option>

                    {loadOptions
                      .filter((option: any) => form.orgLoadOptionIds.includes(option.id))
                      .sort((a, b) => String(a.name ?? '').localeCompare(String(b.name ?? '')))
                      .map((option: any) => (
                        <option key={option.id} value={option.id}>
                          {option.name}

                          {option.capacityLabel ? ` (${option.capacityLabel} tons)` : ''}
                        </option>
                      ))}
                  </select>

                  <div className="muted tiny">
                    This method will be preselected when the customer chooses this delivery
                    location.
                  </div>
                </div>
              )}

              {/* CARRIER RATES */}
              <div className="customer-carrier-rates">
                <div className="customer-carrier-rates__toolbar">
                  <div>
                    <div
                      style={{
                        fontSize: 15,
                        fontWeight: 700,
                      }}
                    >
                      Delivery Rate by Warehouse ($/ton)
                    </div>

                    <div className="muted tiny">
                      Southern delivery rate from each loading location to this address (per ton).{' '}
                      {perItemPricing
                        ? 'Only warehouses containing products enabled for this delivery location are displayed.'
                        : 'Warehouses containing organization products are displayed.'}
                    </div>
                  </div>

                  <div
                    className="muted tiny"
                    style={{
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {configuredFormRateCount} of {availableWarehousesForForm.length} configured
                  </div>
                </div>

                {availableWarehousesForForm.length === 0 ? (
                  <div
                    className="muted"
                    style={{
                      padding: 16,

                      border: '1px solid #e5e7eb',

                      borderRadius: 8,
                    }}
                  >
                    {perItemPricing
                      ? 'Enable at least one product that is assigned to a warehouse before configuring carrier rates.'
                      : 'No warehouses are currently available for carrier-rate configuration.'}
                  </div>
                ) : (
                  <>
                    <div className="customer-carrier-rates__toolbar">
                      <div className="customer-carrier-rates__search">
                        <SearchInput
                          value={warehouseSearch}
                          onValueChange={setWarehouseSearch}
                          placeholder="Search warehouses..."
                          aria-label="Search warehouses"
                        />
                      </div>

                      <label className="customer-carrier-rates__configured-toggle">
                        <input
                          type="checkbox"
                          checked={showConfiguredOnly}
                          onChange={(event) => setShowConfiguredOnly(event.target.checked)}
                        />

                        <span className="customer-carrier-rates__checkbox">
                          <span className="customer-carrier-rates__checkbox-mark">✓</span>
                        </span>

                        <span>Configured only</span>
                      </label>
                    </div>

                    <div className="customer-carrier-rates__list">
                      {filteredWarehouses.length === 0 ? (
                        <div
                          className="muted"
                          style={{
                            padding: 16,
                          }}
                        >
                          No warehouses match the current filters.
                        </div>
                      ) : (
                        filteredWarehouses.map((warehouse) => {
                          const value = form.carrierRates?.[warehouse.id] ?? '';

                          const isConfigured = String(value).trim() !== '';

                          return (
                            <div key={warehouse.id} className="customer-carrier-rates__row">
                              <div className="customer-carrier-rates__warehouse">
                                <div className="name">{warehouse.name}</div>
                              </div>

                              <div className="customer-carrier-rates__input">
                                <span className="currency">$</span>

                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={value}
                                  onChange={(event) =>
                                    updateCarrierRate(
                                      warehouse.id,

                                      event.target.value
                                    )
                                  }
                                  placeholder="Not configured"
                                  aria-label={`Delivery rate for ${warehouse.name}`}
                                  style={{
                                    width: '100%',

                                    paddingLeft: 24,
                                  }}
                                />
                              </div>

                              <button
                                type="button"
                                className="btn btn--ghost btn--sm customer-carrier-rates__clear"
                                onClick={() => clearCarrierRate(warehouse.id)}
                                disabled={!isConfigured}
                              >
                                Clear
                              </button>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </>
                )}
              </div>

              {/* SUBUSERS */}
              <div
                className="form-control"
                style={{
                  gridColumn: '1 / -1',

                  marginTop: 24,
                }}
              >
                <label>Subusers With Access</label>

                {form.customerUsers.length === 0 ? (
                  <div className="muted tiny">
                    No customer subusers have access to this address.
                  </div>
                ) : (
                  <div className="customer-address-user-list">
                    {form.customerUsers.map((customerUser: any) => (
                      <div key={customerUser.id} className="customer-address-user-list__item">
                        <div>
                          <div className="customer-address-user-list__name">
                            {getCustomerUserDisplayName(customerUser)}
                          </div>

                          {customerUser.name && (
                            <div className="muted tiny">{customerUser.email}</div>
                          )}
                        </div>

                        <div className="muted tiny">
                          {!customerUser.isActive ? 'Inactive' : customerUser.status}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="muted tiny">
                  Address access is managed from the customer subuser settings.
                </div>
              </div>

              <div className="customer-address-editor-actions">
                <div className="customer-address-editor-actions__status">
                  {hasUnsavedChanges ? (
                    <span className="customer-address-unsaved-indicator">Unsaved changes</span>
                  ) : (
                    <span className="muted tiny">All changes saved</span>
                  )}
                </div>

                <div className="customer-address-editor-actions__buttons">
                  <button
                    type="button"
                    className="btn btn--primary"
                    onClick={onSave}
                    disabled={isSaving || !hasUnsavedChanges}
                  >
                    {isSaving ? 'Saving…' : 'Save'}
                  </button>

                  <button
                    type="button"
                    className="btn btn--ghost"
                    onClick={handleCancelEdit}
                    disabled={isSaving}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
